import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, User, Mail, Shield, CreditCard, Ban, BanIcon } from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { getAdminUserList, updateUserRole, updateUserDetails } from '@/api/admin';

export const UserEditPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user data
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['adminUserList'],
    queryFn: getAdminUserList,
  });

  const user = users.find((u) => u.id === userId);

  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'user',
    subscription_status: user?.subscription_status || 'free',
    is_banned: user?.is_banned || false,
  });

  // Update mutations
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'user' | 'admin' }) =>
      updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      toast({
        title: "Success",
        description: "User role updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    },
  });

  const updateDetailsMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: { subscription_status?: string; is_banned?: boolean } }) =>
      updateUserDetails(userId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      toast({
        title: "Success",
        description: "User details updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user details",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      // Update role if changed
      if (formData.role !== user.role) {
        await updateRoleMutation.mutateAsync({ userId: user.id, role: formData.role as 'user' | 'admin' });
      }

      // Update details if changed
      const detailsChanged = 
        formData.subscription_status !== user.subscription_status ||
        formData.is_banned !== user.is_banned;

      if (detailsChanged) {
        await updateDetailsMutation.mutateAsync({
          userId: user.id,
          updates: {
            subscription_status: formData.subscription_status,
            is_banned: formData.is_banned,
          },
        });
      }

      // Navigate back to user details
      navigate(`/admin/users/${userId}`);
    } catch (error) {
      // Error handling is done in the mutation handlers
    }
  };

  const handleCancel = () => {
    navigate(`/admin/users/${userId}`);
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading user details...</span>
        </div>
      </PortalLayout>
    );
  }

  if (error || !user) {
    return (
      <PortalLayout>
        <div className="text-center py-20">
          <p className="text-red-600">User not found or error loading user details</p>
          <Button onClick={() => navigate('/admin/users')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit User</h1>
            <p className="text-gray-600">Modify user details and settings for {user.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>User Profile</span>
              </CardTitle>
              <CardDescription>
                Basic user information and identity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter user's name"
                    disabled // Name updates require separate API
                  />
                  <p className="text-xs text-gray-500">Name updates require separate profile management</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter user's email"
                    disabled // Email updates require separate API
                  />
                  <p className="text-xs text-gray-500">Email updates require separate auth management</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>User Role & Permissions</span>
              </CardTitle>
              <CardDescription>
                Control user access level and permissions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {formData.role === 'admin' ? 'Admin users have full access to all features' : 'Regular users have limited access'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Subscription & Billing</span>
              </CardTitle>
              <CardDescription>
                Manage user subscription status and billing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subscription_status">Subscription Status</Label>
                <Select value={formData.subscription_status} onValueChange={(value) => setFormData(prev => ({ ...prev, subscription_status: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subscription status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  This affects the user's access to premium features
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BanIcon className="h-5 w-5" />
                <span>Account Status</span>
              </CardTitle>
              <CardDescription>
                Control user account access and restrictions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_banned"
                  checked={formData.is_banned}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_banned: checked }))}
                />
                <Label htmlFor="is_banned">Ban User</Label>
              </div>
              <p className="text-xs text-gray-500">
                {formData.is_banned ? 'User is banned and cannot access the platform' : 'User has normal access to the platform'}
              </p>
            </CardContent>
          </Card>

          <Separator />

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateRoleMutation.isPending || updateDetailsMutation.isPending}
            >
              {(updateRoleMutation.isPending || updateDetailsMutation.isPending) ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
};

export default UserEditPage;
